import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/nailApi";
import { useI18n } from "../i18n/I18nContext";

const SESSION_KEY = "nailEvent.aiSession";

export default function AIChat({ eventSlug, brand = "AsterPOS AI Business Advisor", poweredBy = "AsterPOS Corp" }) {
  const { lang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: lang === "en"
          ? `Hi! I'm the ${brand}. Ask me anything about the event, your salon business, taxes, HR, or AsterPOS. I can also help in Vietnamese.`
          : `Xin chào! Tôi là ${brand}. Hỏi tôi bất cứ điều gì về sự kiện, vận hành tiệm nail, thuế, nhân sự, hoặc AsterPOS. Tôi cũng trả lời được bằng tiếng Anh.`,
      }]);
    }
  }, [open, lang, brand]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const msg = text.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setText("");
    setSending(true);
    try {
      const r = await api.post("/ai/chat", {
        message: msg,
        session_id: sessionId || undefined,
        language: lang,
        event_slug: eventSlug,
      });
      if (r.data.session_id && !sessionId) {
        setSessionId(r.data.session_id);
        sessionStorage.setItem(SESSION_KEY, r.data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, escalate: r.data.escalate }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: lang === "en" ? "Sorry, I'm temporarily unavailable. Please try again." : "Xin lỗi, AI tạm gián đoạn. Vui lòng thử lại sau.",
        escalate: true,
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          data-testid="ai-chat-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 bg-espresso text-linen rounded-full px-5 py-3.5 shadow-[0_18px_40px_-12px_rgba(44,36,27,0.5)] hover:-translate-y-0.5 hover:bg-espresso-hover transition-all flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4 text-rose-accent" />
          <MessageCircle className="w-4 h-4" />
          <span className="text-sm font-medium hidden sm:inline">{lang === "en" ? "Ask the Advisor" : "Hỏi Cố Vấn AI"}</span>
        </button>
      )}

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-3 right-3 z-50 w-[min(420px,calc(100vw-1.5rem))] h-[min(620px,calc(100vh-2rem))] bg-white rounded-3xl border border-espresso/10 shadow-[0_30px_80px_-30px_rgba(44,36,27,0.6)] overflow-hidden flex flex-col"
        >
          <div className="px-5 py-4 border-b border-espresso/10 bg-linen-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-rose-accent" />
              <div>
                <div className="font-serif text-lg text-espresso leading-none">{brand}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted mt-1">{lang === "en" ? "Powered by" : "Được cung cấp bởi"} {poweredBy}</div>
              </div>
            </div>
            <button
              type="button"
              data-testid="ai-chat-close"
              onClick={() => setOpen(false)}
              className="text-espresso-muted hover:text-espresso p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-linen">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${i}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-espresso text-linen"
                    : "bg-white border border-espresso/10 text-espresso"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-espresso/10 rounded-2xl px-4 py-2.5 text-sm text-espresso-muted inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> {lang === "en" ? "Thinking..." : "Đang suy nghĩ..."}
                </div>
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-espresso/10 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="ai-chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKey}
                rows={1}
                placeholder={lang === "en" ? "Type your question..." : "Nhập câu hỏi của bạn..."}
                className="flex-1 resize-none bg-linen-100 border border-espresso/10 rounded-2xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso/40 max-h-32"
              />
              <button
                type="button"
                data-testid="ai-chat-send"
                onClick={send}
                disabled={sending || !text.trim()}
                className="bg-espresso text-linen rounded-full w-11 h-11 flex items-center justify-center disabled:opacity-50 hover:bg-espresso-hover transition-all"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
